/**
 * bendPath.ts — pure SVG path builder for edges with user-authored bends.
 *
 * Given the source point, target point, source/target handle sides, and any
 * bend points in between, returns an SVG `d` string + a label anchor.
 *
 * The edge is treated as a chain: [src, ...bends, tgt]. Each consecutive pair
 * is drawn as one segment, and each bend applies one of two modes to the TURN
 * at that point:
 *
 *  - `sharp`  — a straight-cornered polyline through the point (no rounding).
 *                The path is a sequence of `L` commands through the bend.
 *  - `smooth` — a rounded corner. The path goes straight toward the bend,
 *                then a single quadratic-bezier `Q` cuts the corner using two
 *                points a fraction of the way back along each incident edge.
 *                (We used to use an SVG arc `A`, but on tight turns the arc's
 *                large-arc/sweep flags degenerate into a near-full circle —
 *                the "path around a circle" bug. A Q curve is stable for any
 *                turn angle and reads as a soft fillet.)
 *
 * Mixing modes on a multi-bend edge works: each bend's mode applies to the
 * turn at that point.
 *
 * Pure: no React, no RF. Tested by bendPath.test.ts.
 */
import { Position } from '@xyflow/react';

import type { Bend } from '../engine/types';

export interface PathResult {
  d: string;
  /** Where the edge label sits — the chain midpoint. */
  labelX: number;
  labelY: number;
}

interface Pt {
  x: number;
  y: number;
}

/** Radius (px) of the rounded corner at a `smooth` bend. The corner-cutting
 *  points sit this far back from the bend along each incident segment. */
const SMOOTH_RADIUS = 28;
/** The unbent (no-bends) edge's bezier control offset — how far the control
 *  point pulls along the handle direction. Matches RF's getBezierPath. */
const DEFAULT_OFFSET = 80;

/** Entry point: build the full chain's path. */
export function bendPath(
  src: Pt,
  tgt: Pt,
  srcPos: Position,
  tgtPos: Position,
  bends: Bend[] = [],
): PathResult {
  if (bends.length === 0) {
    // No bends — defer to the simple two-point bezier (a single cubic with
    // control points pulled along the handle directions). This matches RF's
    // getBezierPath so an unbent edge looks identical to before.
    return twoPointBezier(src, tgt, srcPos, tgtPos);
  }

  // Build the chain: src → bend[0] → ... → bend[n-1] → tgt. Each bend carries
  // its mode; the endpoints have none (the turn AT an endpoint is governed by
  // the nearest bend or by the handle side for the unbent case).
  const chain: Array<Pt & { mode?: Bend['mode'] }> = [
    { ...src },
    ...bends.map((b) => ({ x: b.x, y: b.y, mode: b.mode })),
    { ...tgt },
  ];

  const parts: string[] = [`M ${src.x} ${src.y}`];
  for (let i = 1; i < chain.length; i++) {
    const prev = chain[i - 1];
    const cur = chain[i];
    const next = chain[i + 1];

    if (next === undefined) {
      // Last segment: bend[n-1] → tgt. The turn AT bend[n-1] (= `prev`) was
      // already drawn when we left it (in the previous iteration); here we
      // just close out to the target. Sharp: straight line. Smooth: the
      // corner-cut's outgoing point was already emitted, so straight to tgt.
      parts.push(`L ${tgt.x} ${tgt.y}`);
    } else if (cur.mode === 'smooth') {
      // The turn at `cur`. Emit the IN side now (straight to a point just
      // before the bend), then a quadratic Q whose control IS the bend point
      // and whose end is a point just past the bend on the OUT side. The next
      // iteration continues straight from that out-point toward `next`.
      const inStop = shorten(cur, prev, SMOOTH_RADIUS);
      const outStart = shorten(cur, next, SMOOTH_RADIUS);
      parts.push(`L ${inStop.x} ${inStop.y}`);
      parts.push(`Q ${cur.x} ${cur.y} ${outStart.x} ${outStart.y}`);
    } else {
      // sharp (default): a hard corner right at the bend point.
      parts.push(`L ${cur.x} ${cur.y}`);
    }
  }

  const d = parts.join(' ');
  // Label anchor: midpoint of the chain (or the middle bend if there's an odd
  // count). Falls back to the geometric midpoint of src/tgt.
  const midIdx = Math.floor(chain.length / 2);
  const label = chain[Math.min(midIdx, chain.length - 1)];
  return { d, labelX: label.x, labelY: label.y };
}

/** Two-point cubic bezier — the unbent edge. Mirrors RF's getBezierPath. */
function twoPointBezier(src: Pt, tgt: Pt, srcPos: Position, tgtPos: Position): PathResult {
  const [c1, c2] = controlPoints(src, tgt, srcPos, tgtPos);
  const d = `M ${src.x} ${src.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${tgt.x} ${tgt.y}`;
  const labelX = (src.x + tgt.x) / 2;
  const labelY = (src.y + tgt.y) / 2;
  return { d, labelX, labelY };
}

/** Build the two control points for a cubic between a→b. The control points
 *  sit DEFAULT_OFFSET px along the handle direction from each end. */
function controlPoints(a: Pt, b: Pt, aDir: Position, bDir: Position): [Pt, Pt] {
  return [offset(a, aDir, DEFAULT_OFFSET), offset(b, bDir, DEFAULT_OFFSET)];
}

/** Move a point `dist` px along a Position direction. */
function offset(p: Pt, dir: Position, dist: number): Pt {
  switch (dir) {
    case Position.Left: return { x: p.x - dist, y: p.y };
    case Position.Right: return { x: p.x + dist, y: p.y };
    case Position.Top: return { x: p.x, y: p.y - dist };
    case Position.Bottom: return { x: p.x, y: p.y + dist };
  }
}

/** Shorten the segment from→to by `dist` px at the `to` end; returns the new
 *  endpoint just before `to`. Used to leave room for the quadratic corner at
 *  a smooth bend. If the segment is shorter than `dist`, collapses to `from`
 *  (so a degenerate corner just becomes a sharp point rather than flipping
 *  past the bend). */
function shorten(from: Pt, to: Pt, dist: number): Pt {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len <= dist) return { ...from };
  const t = (len - dist) / len;
  return { x: from.x + dx * t, y: from.y + dy * t };
}
